import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export const qtyInputDomId = (rowKey) => `invoice-qty-${rowKey}`;
export const priceInputDomId = (rowKey) => `invoice-price-${rowKey}`;

const selectInputText = (el) => {
  if (!el || typeof el.select !== "function") return;
  try {
    el.select();
  } catch {
    /* ignore */
  }
};

const focusAndSelectInput = (el) => {
  if (!el) return false;
  el.focus({ preventScroll: true });
  requestAnimationFrame(() => selectInputText(el));
  return document.activeElement === el;
};
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../store/Slices/ProductSlice";
import { WarningToast, ErrorToast } from "../../utils/ShowToast";
import {
  DeleteInvoiceItemAPI,
  DeleteReturnItemAPI,
  UpdateInvoiceItemApi,
  UpdateReturnItemApi,
} from "../../ApiRequests";
import { FiPlus, FiTrash2 } from "react-icons/fi";

export const emptyInvoiceItemRow = () => ({
  _key: `${Date.now()}-${Math.random()}`,
  productLineId: "",
  itemId: "",
  name: "",
  code: "",
  qty: "",
  purchase: "",
  price: "",
  unit: "",
  amount: "",
});

/** Map saved invoice Product lines → table rows (edit invoice UI) */
export const mapInvoiceProductsToItemRows = (products = [], stockList = []) => {
  const rows = (products || []).map((p) => {
    const stockId = p.itemId?._id || p.itemId;
    const stock = (stockList || []).find(
      (s) => String(s._id) === String(stockId)
    );
    const itemId = stockId ? String(stockId) : "";
    const qtyNum = Number(p.qty) || 0;
    return {
      _key: String(p._id),
      productLineId: String(p._id),
      itemId,
      // Credit these back into available stock while editing (already deducted)
      _originalItemId: itemId,
      _originalQty: qtyNum,
      name: p.name || "",
      code: stock ? String(stock.code ?? "") : "",
      qty: p.qty != null && p.qty !== "" ? String(p.qty) : "",
      purchase: p.purchase ?? "",
      price: p.price ?? "",
      unit: p.unit || "",
      amount: p.amount ?? "",
    };
  });
  return rows.length ? rows : [emptyInvoiceItemRow()];
};

const emptyRow = emptyInvoiceItemRow;

const norm = (value) => String(value ?? "").trim().toLowerCase();

/** Same lookup as AddMultipleStock — exact match on code OR name */
const findProductByInput = (products, value) => {
  if (!value || !Array.isArray(products) || !products.length) return null;
  const query = norm(value);
  if (!query) return null;
  return (
    products.find((item) => norm(item?.name) === query || norm(item?.code) === query) ??
    null
  );
};

const filterProducts = (products, value, limit = 12) => {
  const q = norm(value);
  if (!q) return products.slice(0, limit);
  return products
    .filter(
      (p) =>
        norm(String(p.code ?? "")).includes(q) ||
        norm(String(p.name ?? "")).includes(q)
    )
    .slice(0, limit);
};

/** Code field with dropdown (replaces datalist — reliable qty focus after pick). */
const InvoiceItemCodeField = React.memo(({
  rowKey,
  code,
  products,
  inputClass,
  onDraftChange,
  onSelectProduct,
  onShiftEnter,
  inputRef,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [menuRect, setMenuRect] = useState(null);
  const localInputRef = useRef(null);
  const listRef = useRef(null);
  const blurTimerRef = useRef(null);
  const query = String(code ?? "");

  const setInputRef = useCallback(
    (el) => {
      localInputRef.current = el;
      if (typeof inputRef === "function") inputRef(el);
      else if (inputRef) inputRef.current = el;
    },
    [inputRef]
  );

  const matches = useMemo(
    () => filterProducts(products, query),
    [products, query]
  );

  const showMenu = open && query.trim().length > 0 && matches.length > 0;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [query]);

  useEffect(() => {
    if (!showMenu || highlightIndex < 0) return;
    const option = listRef.current?.children[highlightIndex];
    option?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, showMenu]);

  const updateMenuRect = useCallback(() => {
    const el = localInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
    });
  }, []);

  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuRect(null);
      return;
    }
    updateMenuRect();
    const onReflow = () => updateMenuRect();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [showMenu, query, matches.length, updateMenuRect]);

  useEffect(
    () => () => {
      if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    },
    []
  );

  const pickProduct = (item) => {
    if (!item) return;
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    setOpen(false);
    setHighlightIndex(-1);
    onSelectProduct(rowKey, item);
  };

  const handleInputChange = (value) => {
    onDraftChange(rowKey, value);
    setOpen(Boolean(String(value).trim()));
  };

  const scheduleClose = () => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    blurTimerRef.current = window.setTimeout(() => {
      const active = document.activeElement;
      if (listRef.current?.contains(active)) return;
      setOpen(false);
      setHighlightIndex(-1);
    }, 160);
  };

  const menu =
    showMenu && menuRect
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="fixed z-[9999] max-h-52 overflow-y-auto rounded-lg border border-line bg-card shadow-lifted py-1 scrollable"
            style={{
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {matches.map((item, index) => (
              <li
                key={item._id}
                id={`invoice-code-option-${rowKey}-${index}`}
                role="option"
                aria-selected={index === highlightIndex}
                className={
                  index === highlightIndex ? "bg-main/12" : undefined
                }
                onMouseEnter={() => setHighlightIndex(index)}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  className={`w-full text-left px-2.5 py-1.5 text-sm focus:outline-none ${
                    index === highlightIndex
                      ? "bg-main/10"
                      : "hover:bg-main/10"
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickProduct(item);
                  }}
                >
                  <span className="font-semibold text-main tabular-nums">
                    {item.code}
                  </span>
                  <span className="text-inkSecondary ml-2 truncate">
                    {item.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className="relative">
      <input
        ref={setInputRef}
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => {
          if (query.trim()) setOpen(true);
        }}
        onBlur={scheduleClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            setHighlightIndex(-1);
            return;
          }
          if (e.key === "ArrowDown" && matches.length && query.trim()) {
            e.preventDefault();
            setOpen(true);
            setHighlightIndex((prev) =>
              prev < 0 ? 0 : Math.min(prev + 1, matches.length - 1)
            );
            return;
          }
          if (e.key === "ArrowUp" && matches.length && query.trim()) {
            e.preventDefault();
            setOpen(true);
            setHighlightIndex((prev) =>
              prev <= 0 ? 0 : prev - 1
            );
            return;
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
            if (highlightIndex >= 0 && matches[highlightIndex]) {
              pickProduct(matches[highlightIndex]);
            } else {
              const exact = findProductByInput(products, query);
              if (exact) pickProduct(exact);
              else if (query.trim()) WarningToast(`Item not found: "${query}"`);
            }
            setHighlightIndex(-1);
            return;
          }
          if (e.key === "Tab" && !e.shiftKey) {
            const exact = findProductByInput(products, query);
            if (exact) {
              e.preventDefault();
              pickProduct(exact);
            }
            return;
          }
          onShiftEnter?.(e);
        }}
        placeholder="Code or search"
        className={inputClass}
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={showMenu}
        aria-activedescendant={
          showMenu && highlightIndex >= 0
            ? `invoice-code-option-${rowKey}-${highlightIndex}`
            : undefined
        }
      />
      {menu}
    </div>
  );
});
InvoiceItemCodeField.displayName = "InvoiceItemCodeField";

const CreateInvoiceItemsTable = ({
  items,
  setItems,
  saleType,
  customerAccountType,
  editInvoiceId,
  editReturnId,
  editCustomerId,
  editIsGuest = false,
  onInvoiceMutated,
  skipStockValidation = false,
  variant = "default",
}) => {
  const isPos = variant === "pos";
  const editDocumentId = editInvoiceId || editReturnId;
  const isReturnEdit = Boolean(editReturnId);
  const dispatch = useDispatch();
  const ItemState = useSelector((state) => state.ItemsState);
  const codeInputRefs = useRef({});
  const codeRefAttachers = useRef({});
  const qtyInputRefs = useRef({});
  const itemsRef = useRef(items);
  const pendingCodeFocusKey = useRef(null);
  const pendingQtyFocusKey = useRef(null);
  const tableWrapRef = useRef(null);
  const prevCustomerAccountType = useRef(customerAccountType);

  itemsRef.current = items;

  const focusQtyField = useCallback((key) => {
    const el =
      document.getElementById(qtyInputDomId(key)) ||
      qtyInputRefs.current[key];
    if (!el) return false;

    const wrap = tableWrapRef.current;
    const scrollLeft = wrap?.scrollLeft ?? 0;
    const winX = window.scrollX;
    const winY = window.scrollY;

    el.focus({ preventScroll: true });
    requestAnimationFrame(() => selectInputText(el));

    if (wrap) wrap.scrollLeft = scrollLeft;
    window.scrollTo(winX, winY);

    return document.activeElement === el;
  }, []);

  useLayoutEffect(() => {
    const key = pendingQtyFocusKey.current;
    if (!key) return;
    const row = items.find((r) => r._key === key);
    if (!row?.itemId) return;

    const wrap = tableWrapRef.current;
    const scrollLeft = wrap?.scrollLeft ?? 0;
    const winX = window.scrollX;
    const winY = window.scrollY;

    if (focusQtyField(key)) pendingQtyFocusKey.current = null;

    if (wrap) wrap.scrollLeft = scrollLeft;
    window.scrollTo(winX, winY);
  }, [items, focusQtyField]);

  const products = useMemo(() => {
    const list = Array.isArray(ItemState.data) ? ItemState.data : [];
    return list.filter((item) => item && item.isDeleted !== true);
  }, [ItemState.data]);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (!items.length) {
      setItems([emptyRow()]);
    }
  }, [items.length, setItems]);

  const focusInput = (refs, key) => {
    const el = refs.current[key];
    if (!el) return false;
    el.focus();
    return true;
  };

  useEffect(() => {
    const key = pendingCodeFocusKey.current;
    if (!key) return;
    if (focusInput(codeInputRefs, key)) pendingCodeFocusKey.current = null;
  }, [items]);

  const getRetailPrice = (item) =>
    Number(customerAccountType) === 2 ? item.sale_shop : item.sale;

  const buildRowFromItem = (row, item) => {
    const retail = getRetailPrice(item);
    const qty = row.qty ?? "";
    const amount =
      qty !== "" && retail !== undefined && retail !== ""
        ? Number(qty) * Number(retail)
        : "";
    return {
      ...row,
      code: String(item.code ?? "").trim(),
      itemId: String(item._id),
      name: item.name || "",
      unit: item.unit || "",
      purchase: item.purchase ?? "",
      price: retail ?? "",
      amount,
    };
  };

  const commitItemToRow = useCallback(
    (key, item) => {
      setItems((prev) =>
        prev.map((row) =>
          row._key === key ? buildRowFromItem(row, item) : row
        )
      );
    },
    [setItems, customerAccountType]
  );

  const getCodeInputRef = useCallback((rowKey) => {
    if (!codeRefAttachers.current[rowKey]) {
      codeRefAttachers.current[rowKey] = (el) => {
        if (el) codeInputRefs.current[rowKey] = el;
        else delete codeInputRefs.current[rowKey];
      };
    }
    return codeRefAttachers.current[rowKey];
  }, []);

  const qtyRefAttachers = useRef({});

  const getQtyRef = useCallback((rowKey) => {
    if (!qtyRefAttachers.current[rowKey]) {
      qtyRefAttachers.current[rowKey] = (el) => {
        if (el) qtyInputRefs.current[rowKey] = el;
        else delete qtyInputRefs.current[rowKey];
      };
    }
    return qtyRefAttachers.current[rowKey];
  }, []);

  /** Shop vs retail price — only when account type changes (not on every products fetch). */
  useEffect(() => {
    if (customerAccountType === undefined || customerAccountType === null) return;
    if (prevCustomerAccountType.current === customerAccountType) return;
    prevCustomerAccountType.current = customerAccountType;
    if (!itemsRef.current.some((r) => r.itemId)) return;

    setItems((prev) =>
      prev.map((row) => {
        if (!row.itemId) return row;
        // Keep saved invoice/return line prices on edit hydrate / type seed
        if (row.productLineId) return row;
        const item = products.find(
          (p) => String(p._id) === String(row.itemId)
        );
        if (!item) return row;
        return buildRowFromItem(row, item);
      })
    );
  }, [customerAccountType, products, setItems]);

  const clearItemFields = (row, codeValue = "") => ({
    ...row,
    code: codeValue,
    productLineId: "",
    itemId: "",
    _originalItemId: "",
    _originalQty: 0,
    name: "",
    unit: "",
    purchase: "",
    price: "",
    amount: "",
  });

  const isRowComplete = (row) =>
    Boolean(row.itemId && row.qty && row.price && row.amount);

  const persistExistingLine = async (row) => {
    if (!editDocumentId || !row.productLineId) return;
    try {
      if (isReturnEdit) {
        await UpdateReturnItemApi(row.productLineId, {
          return_id: editReturnId,
          itemId: row.itemId,
          name: row.name,
          qty: Number(row.qty),
          unit: row.unit,
          price: Number(row.price),
          purchase: Number(row.purchase || row.price),
          amount: Number(row.amount),
        });
      } else {
        await UpdateInvoiceItemApi(row.productLineId, {
          invoice_id: editInvoiceId,
          itemId: row.itemId,
          name: row.name,
          qty: Number(row.qty),
          unit: row.unit,
          price: Number(row.price),
          purchase: Number(row.purchase || row.price),
          amount: Number(row.amount),
        });
      }
      onInvoiceMutated?.();
    } catch (err) {
      ErrorToast(err.response?.data?.error?.msg || "Failed to update item");
    }
  };

  const afterQtyPriceBlur = async (row) => {
    if (!editDocumentId) return;
    // Only live-update existing lines. New rows are created once on Save via
    // syncEditDocumentItems — creating here too caused a duplicate item on submit.
    if (row.productLineId) {
      await persistExistingLine(row);
    }
  };

  const handleCodeDraftChange = (key, value) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row._key !== key) return row;
        // Only wipe selected line-item fields when user edits after a pick
        if (row.itemId) return clearItemFields(row, value);
        if (row.code === value) return row;
        return { ...row, code: value };
      })
    );
  };

  const handleSelectProduct = (key, item) => {
    pendingQtyFocusKey.current = key;
    commitItemToRow(key, item);
  };

  const insertRowAfter = (afterKey) => {
    const newRow = emptyRow();
    pendingCodeFocusKey.current = newRow._key;
    setItems((prev) => {
      const idx = prev.findIndex((r) => r._key === afterKey);
      if (idx === -1) return [...prev, newRow];
      const next = [...prev];
      next.splice(idx + 1, 0, newRow);
      return next;
    });
  };

  const handleShiftEnterNewRow = (e, rowKey) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      insertRowAfter(rowKey);
    }
  };

  const handleQtyChange = (key, value, row) => {
    setItems((prev) =>
      prev.map((r) => {
        if (r._key !== key) return r;
        let price = r.price;
        if (r.itemId && (price === "" || price == null)) {
          const item = products.find((p) => String(p._id) === String(r.itemId));
          if (item) {
            const retail = getRetailPrice(item);
            if (retail !== undefined && retail !== "") price = retail;
          }
        }
        const amount =
          value !== "" && price !== ""
            ? Number(value) * Number(price)
            : "";
        return {
          ...r,
          qty: value,
          price,
          amount: amount === "" ? "" : amount,
        };
      })
    );
  };

  const handlePriceChange = (key, value, row) => {
    const amount =
      row.qty !== "" && value !== ""
        ? Number(row.qty) * Number(value)
        : "";
    setItems((prev) =>
      prev.map((r) =>
        r._key === key ? { ...r, price: value, amount: amount === "" ? "" : amount } : r
      )
    );
  };

  const validateStock = (row) => {
    if (skipStockValidation) return true;
    if (saleType !== "SALE" || !row.itemId) return true;
    const stock = products.find(
      (dt) => String(dt._id) === String(row.itemId)
    );
    if (!stock) return true;

    // Edit sale: stock already had this invoice's lines deducted — credit them back
    let credited = 0;
    if (editDocumentId && !isReturnEdit) {
      for (const r of itemsRef.current || []) {
        const origId = String(r._originalItemId || "");
        if (!origId || origId !== String(row.itemId)) continue;
        credited += Number(r._originalQty || 0);
      }
    }

    const needed = (itemsRef.current || [])
      .filter((r) => String(r.itemId) === String(row.itemId) && r.qty !== "")
      .reduce((sum, r) => sum + (Number(r.qty) || 0), 0);

    const available = Number(stock.qty || 0) + credited;
    if (needed > available) {
      WarningToast(`Quantity must be ≤ ${available} for ${row.name || stock.name}`);
      return false;
    }
    return true;
  };

  const removeRow = async (key) => {
    const row = itemsRef.current.find((r) => r._key === key);
    if (editDocumentId && row?.productLineId) {
      try {
        if (isReturnEdit) {
          await DeleteReturnItemAPI(row.productLineId);
        } else {
          await DeleteInvoiceItemAPI(row.productLineId);
        }
        onInvoiceMutated?.();
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Failed to delete item");
        return;
      }
    }
    delete qtyRefAttachers.current[key];
    delete codeRefAttachers.current[key];
    setItems((prev) => {
      const next = prev.filter((r) => r._key !== key);
      return next.length ? next : [emptyRow()];
    });
  };

  const canAddNewLines =
    !editDocumentId || Boolean(editCustomerId) || editIsGuest;

  const advanceFromPriceField = (e, rowKey) => {
    const isTab = e.key === "Tab" && !e.shiftKey;
    const isEnter = e.key === "Enter" && !e.shiftKey;
    if (!isTab && !isEnter) return;

    const latest = itemsRef.current.find((r) => r._key === rowKey);
    if (!latest || !isRowComplete(latest)) return;

    if (!validateStock(latest)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const rowIndex = itemsRef.current.findIndex((r) => r._key === rowKey);
    const isLastRow = rowIndex === itemsRef.current.length - 1;

    if (isLastRow) {
      if (!canAddNewLines) return;
      insertRowAfter(rowKey);
      return;
    }

    const nextKey = itemsRef.current[rowIndex + 1]?._key;
    if (nextKey) {
      pendingCodeFocusKey.current = nextKey;
      requestAnimationFrame(() => focusInput(codeInputRefs, nextKey));
    }
  };

  const addRow = () => {
    if (!canAddNewLines) {
      WarningToast(
        editIsGuest
          ? "Cannot add items on guest invoice here"
          : "Select a customer before adding items"
      );
      return;
    }
    const newRow = emptyRow();
    pendingCodeFocusKey.current = newRow._key;
    setItems((prev) => [...prev, newRow]);
  };

  const thClass = isPos
    ? "px-2 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-white whitespace-nowrap sticky top-0 z-10 bg-main"
    : "px-2 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-white whitespace-nowrap";
  const tdClass = isPos
    ? "px-1.5 py-1 align-middle"
    : "px-1.5 py-1.5 align-top";
  const inputClass = isPos
    ? "w-full min-w-0 h-10 px-2.5 text-sm font-semibold border border-line rounded-lg outline-none focus:border-main focus:ring-2 focus:ring-main/20 bg-card text-ink"
    : "w-full min-w-0 px-2 py-1.5 text-sm border border-line rounded-md outline-none focus:border-main focus:ring-2 focus:ring-main/15 bg-card text-ink";
  const inputReadOnlyClass = isPos
    ? "w-full min-w-0 h-10 px-2.5 text-sm border border-line/80 rounded-lg bg-subtle/80 text-inkSecondary cursor-default"
    : "w-full min-w-0 px-2 py-1.5 text-sm border border-line rounded-md bg-subtle text-inkSecondary cursor-default";
  const inputWideClass = `${inputClass} min-w-[7rem]`;
  const inputAmountClass = isPos
    ? `${inputReadOnlyClass} min-w-[8rem] font-bold text-main tabular-nums`
    : `${inputReadOnlyClass} min-w-[8rem] font-semibold`;
  const rowClass = isPos
    ? "border-b border-line even:bg-subtle/30 hover:bg-main/[0.04] transition-colors"
    : "border-b border-line bg-card hover:bg-subtle/40 transition-colors";

  return (
    <div
      ref={tableWrapRef}
      className="w-full overflow-x-auto overflow-y-visible"
      data-invoice-items-table
    >
      {ItemState.loading && (
        <p className="text-xs text-warning mb-2">Loading items…</p>
      )}
      {!ItemState.loading && products.length === 0 && (
        <p className="text-xs text-danger mb-2">
          No items loaded. Refresh page or check branch login.
        </p>
      )}

      <table
        className={`w-full min-w-[1080px] border-collapse ${
          isPos ? "text-sm" : ""
        }`}
      >
        <thead>
          <tr className="bg-main">
            {isPos && (
              <th className={`${thClass} w-[40px] text-center`}>#</th>
            )}
            <th className={`${thClass} w-[160px]`}>Code</th>
            <th className={`${thClass} min-w-[200px]`}>Item Name</th>
            <th className={`${thClass} w-[120px]`}>Qty</th>
            <th className={`${thClass} w-[110px]`}>Purchase</th>
            <th className={`${thClass} w-[140px]`}>Retail Price</th>
            <th className={`${thClass} w-[72px]`}>Unit</th>
            <th className={`${thClass} w-[140px]`}>Amount</th>
            <th className={`${thClass} w-[56px]`} />
          </tr>
        </thead>
        <tbody>
          {items.map((row, rowIndex) => {
            const canEditQtyPrice = Boolean(row.itemId);
            const stockItem = row.itemId
              ? products.find((p) => String(p._id) === String(row.itemId))
              : null;
            const qtyPlaceholder =
              stockItem != null &&
              stockItem.qty !== undefined &&
              stockItem.qty !== null &&
              stockItem.qty !== ""
                ? `Avail: ${stockItem.qty}`
                : "Qty";
            return (
              <tr key={row._key} className={rowClass}>
                {isPos && (
                  <td className={`${tdClass} text-center`}>
                    <span className="text-xs font-bold text-muted tabular-nums">
                      {rowIndex + 1}
                    </span>
                  </td>
                )}
                <td className={tdClass}>
                  <InvoiceItemCodeField
                    rowKey={row._key}
                    code={row.code}
                    products={products}
                    inputClass={inputClass}
                    onDraftChange={handleCodeDraftChange}
                    onSelectProduct={handleSelectProduct}
                    onShiftEnter={(e) => handleShiftEnterNewRow(e, row._key)}
                    inputRef={getCodeInputRef(row._key)}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    type="text"
                    value={row.name ?? ""}
                    readOnly
                    tabIndex={-1}
                    className={inputReadOnlyClass}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    id={qtyInputDomId(row._key)}
                    ref={getQtyRef(row._key)}
                    type="number"
                    value={row.qty}
                    tabIndex={0}
                    onChange={(e) => {
                      if (!canEditQtyPrice) return;
                      handleQtyChange(row._key, e.target.value, row);
                    }}
                    onFocus={(e) => {
                      requestAnimationFrame(() => selectInputText(e.target));
                    }}
                    onBlur={async () => {
                      if (!validateStock(row)) return;
                      const latest = itemsRef.current.find((r) => r._key === row._key) || row;
                      await afterQtyPriceBlur(latest);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && canEditQtyPrice) {
                        e.preventDefault();
                        focusAndSelectInput(
                          document.getElementById(priceInputDomId(row._key))
                        );
                        return;
                      }
                      if (
                        e.key === "Tab" &&
                        !e.shiftKey &&
                        canEditQtyPrice
                      ) {
                        const priceEl = document.getElementById(
                          priceInputDomId(row._key)
                        );
                        if (priceEl) {
                          e.preventDefault();
                          focusAndSelectInput(priceEl);
                          return;
                        }
                      }
                      handleShiftEnterNewRow(e, row._key);
                    }}
                    placeholder={qtyPlaceholder}
                    className={canEditQtyPrice ? inputWideClass : inputReadOnlyClass}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    type="number"
                    value={row.purchase}
                    readOnly
                    tabIndex={-1}
                    className={inputReadOnlyClass}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    type="number"
                    value={row.price}
                    readOnly={!canEditQtyPrice}
                    id={priceInputDomId(row._key)}
                    onFocus={(e) => {
                      requestAnimationFrame(() => selectInputText(e.target));
                    }}
                    onChange={(e) =>
                      handlePriceChange(row._key, e.target.value, row)
                    }
                    onBlur={async () => {
                      const latest = itemsRef.current.find((r) => r._key === row._key) || row;
                      await afterQtyPriceBlur(latest);
                    }}
                    onKeyDown={(e) => {
                      advanceFromPriceField(e, row._key);
                      handleShiftEnterNewRow(e, row._key);
                    }}
                    placeholder="Price"
                    className={canEditQtyPrice ? inputWideClass : inputReadOnlyClass}
                    tabIndex={canEditQtyPrice ? 0 : -1}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    type="text"
                    value={row.unit}
                    readOnly
                    tabIndex={-1}
                    className={`${inputReadOnlyClass} text-center`}
                  />
                </td>
                <td className={tdClass}>
                  <input
                    type="number"
                    value={row.amount}
                    readOnly
                    tabIndex={-1}
                    className={inputAmountClass}
                  />
                </td>
                <td className={tdClass}>
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      title="Delete row"
                      onClick={() => removeRow(row._key)}
                      className="p-1.5 rounded-md text-danger hover:bg-dangerSoft transition-colors"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className={`text-muted mt-2 ${isPos ? "text-xs" : "text-[11px]"}`}>
        {isPos
          ? "Code → Enter → Qty → Tab → Price → Tab/Enter = next row (when line is filled) · Shift+Enter = insert row"
          : "Type code, pick from dropdown, or Enter — cursor jumps to Qty. Tab from Qty goes to Retail Price. Tab/Enter on Price adds next row when filled. Shift+Enter = new row."}
      </p>

      {canAddNewLines && (
        <div className="flex justify-end gap-2 mt-3">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-main text-white text-sm font-semibold hover:bg-mainHover transition-colors active:scale-[.98]"
          >
            <FiPlus size={16} />
            Add Row
          </button>
        </div>
      )}
    </div>
  );
};

export const getCommittedInvoiceItems = (rows) =>
  rows
    .filter((row) => row.itemId && row.qty && row.price && row.amount)
    .map(({ itemId, name, qty, unit, purchase, price, amount }) => ({
      itemId,
      name,
      qty: Number(qty),
      unit,
      purchase: Number(purchase || price),
      price: Number(price),
      amount: Number(amount),
    }));

/** Same as committed items, but keeps productLineId for edit-invoice submit sync */
export const getCommittedEditInvoiceItems = (rows) =>
  rows
    .filter((row) => row.itemId && row.qty && row.price && row.amount)
    .map(
      ({ productLineId, itemId, name, qty, unit, purchase, price, amount }) => ({
        ...(productLineId ? { productLineId: String(productLineId) } : {}),
        itemId,
        name,
        qty: Number(qty),
        unit,
        purchase: Number(purchase || price),
        price: Number(price),
        amount: Number(amount),
      })
    );

export default CreateInvoiceItemsTable;
